/** @typedef {import('headlight-configurable-application').Pict} Pict */

const MaterialsCascadePicker = require('./PictProvider-InputType-MaterialsCascadePicker.js');

/** @type {Record<string, any>} */
const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'MaterialsTestInjectorPicker',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	AutoSolveWithApp: false,
	Entity: 'Test',
};

/**
 * The Test injector picker (beast B5): the Picker-stack port of MaterialsTestInjector, mirroring the
 * legacy provider 1:1 on the MaterialsCascadePicker base. A narrow selector: the user picks an ACTIVE,
 * INJECTABLE test (committed by Hash) and clicks the template's "Add Test" addon button, which calls
 * `registerTest` — DocumentDynamicSectionManager injects the test's form sections, then the data
 * requirements re-apply for the bound sample. The picked value is a section recipe, NOT form data:
 * `_refreshEntityRecord` is OPTED OUT (no `__Record` snapshot, no cascade — the legacy onDataChange
 * suppressed the record load the same way).
 */
class PictProviderInputTypeMaterialsTestInjectorPicker extends MaterialsCascadePicker
{
	/**
	 * @param {Object} pFable @param {Object} [pOptions] @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DEFAULT_CONFIGURATION, pOptions), pServiceHash);
		/** @type {Pict & { TransactionTracking: any, TempData: any, PictApplication: any }} */
		this.pict;
	}

	/** An injectable Test commits by HASH — the section-registration key (legacy _getDefaultRecordIdentity).
	 *  @param {Record<string, any>} pInput @return {string} */
	_getRecordIdentity(pInput)
	{
		return pInput?.PictForm?.RecordIdentity || 'Hash';
	}

	/**
	 * Mandatory injector scoping, ported from the legacy HandleSearchFilters: only Active, INJECTABLE
	 * tests are offered. Contextual filters paren-wrapped so the appended clauses AND the whole set.
	 * @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex] @param {Object} [pView]
	 * @return {{Filters: Array<string>, BackOffFilters: Array<string>}}
	 */
	getContextualSearchFilters(pInput, pRowIndex, pView)
	{
		const tmpSplit = super.getContextualSearchFilters(pInput, pRowIndex, pView);
		const tmpFilters = tmpSplit.Filters;
		if (tmpFilters.length > 0)
		{
			tmpFilters.unshift('FOP~0~(~0');
			tmpFilters.push('FCP~0~)~0');
		}
		tmpFilters.push('FBV~Active~EQ~1');
		tmpFilters.push('FBV~Injectable~EQ~1');
		return tmpSplit;
	}

	/** Init: default the Hash EntityTag (ported 1:1). */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = { };
		}
		if (pInput.PictForm.EntityTag == null)
		{
			pInput.PictForm.EntityTag = 'Hash';
		}
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/** Tabular twin of the init. */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = { };
		}
		if (pInput.PictForm.EntityTag == null)
		{
			pInput.PictForm.EntityTag = 'Hash';
		}
		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * THE `__Record` OPT-OUT: the injector's value is a section recipe, not form data — the legacy
	 * onDataChange deliberately skipped the record load ("we don't want to load the test record"), so
	 * the broker's snapshot + cascade machinery is suppressed wholesale here.
	 */
	// eslint-disable-next-line no-unused-vars
	_refreshEntityRecord(pView, pInput, pValue, pTransactionGUID, pRowIndex)
	{
		return;
	}

	/**
	 * The bound Sample record — ported VERBATIM, both legacy FIXMEs intact: the PushRecord tracking slot
	 * carries a fresh UUID no sample will ever match (busted-but-works), so resolution effectively rides
	 * the conventional-address fallback.
	 * @return {Record<string, any>|null}
	 */
	_getBoundSampleRecord()
	{
		const tmpPayloadUUID = this.pict.getUUID();
		//FIXME: I think this event is busted - it works but not in a good way
		this.pict.views.PictFormMetacontroller.triggerGlobalInputEvent(`PushRecord:${tmpPayloadUUID}:TempData["${tmpPayloadUUID}"]`);

		let tmpSample = this.pict.TempData[tmpPayloadUUID];
		delete this.pict.TempData[tmpPayloadUUID];

		if (!tmpSample || typeof tmpSample !== 'object' || Array.isArray(tmpSample))
		{
			//FIXME: this is terrible
			const tmpSampleAddress = 'SampleID__Record';
			const tmpViewMarshalDestinationObject = this.pict.resolveStateFromAddress(this.pict.views.PictFormMetacontroller.viewMarshalDestination);
			tmpSample = this.pict.manifest.getValueAtAddress(tmpViewMarshalDestinationObject, tmpSampleAddress);
		}
		return tmpSample;
	}

	/**
	 * Inject the picked test's sections — the template's "Add Test" button target, ported 1:1:
	 * DocumentDynamicSectionManager registers the test (salted, so the same test can inject twice),
	 * then the data requirements recompute + re-apply for the bound sample, then the app re-solves.
	 * @param {string} pTestHash - The test hash to register.
	 * @param {string} pSaltHash - The salt hash to register.
	 * @param {(pError?: Error) => void} [fCallback] - Optional callback function to be called after registration.
	 */
	registerTest(pTestHash, pSaltHash, fCallback)
	{
		if (!pTestHash)
		{
			if (typeof fCallback === 'function')
			{
				fCallback();
			}
			return;
		}
		if (!this.pict.providers.DocumentDynamicSectionManager)
		{
			this.pict.log.error(`MaterialsTestInjectorPicker: DocumentDynamicSectionManager provider not available; cannot register test`, { pTestHash });
			if (typeof fCallback === 'function')
			{
				fCallback();
			}
			return;
		}
		this.pict.providers.DocumentDynamicSectionManager.registerTest(pTestHash, pSaltHash, (pViews) =>
		{
			if (Array.isArray(pViews) && pViews.length > 0)
			{
				const tmpSample = this._getBoundSampleRecord();
				if (!tmpSample)
				{
					this.pict.log.warn(`Unable to look up sample record after test injection; skipping data requirement update`, { pTestHash });
					this.pict.PictApplication.solve();
					return;
				}
				this.pict.providers.DataRequirementsProvider.updateDataRequirementsForManifest(this.pict.views.PictFormMetacontroller.manifestDescription, () =>
				{
					this.pict.providers.DataRequirementsProvider.applyDataRequirements(this.pict.views.PictFormMetacontroller.manifestDescription, tmpSample).catch((pError) =>
					{
						this.pict.log.error(`Error applying data requirements after test injection: ${pError.message || pError}`);
					}).finally(() =>
					{
						this.pict.PictApplication.solve(); //FIXME: this may be a second solve, depending on the data requirements outcome
						if (typeof fCallback === 'function')
						{
							fCallback();
						}
					});
				}, [ pTestHash ]);
			}
		});
	}
}

module.exports = PictProviderInputTypeMaterialsTestInjectorPicker;

module.exports.default_configuration = _DEFAULT_CONFIGURATION;
